import { computed, reactive, ref } from 'vue';
import { guardados, liberarPrevias, paraATela } from './anexos';

// A conversa de teste no modo motor: o envio devolve um pedido e a resposta sai numa consulta a
// cada segundo, porque o turno com ferramentas passa dos 15 s que o servidor dá a uma requisição.
// O runtime corta o turno em 90 s; a tela desiste um pouco depois.
//
// Quem fala com o servidor é o transporte de cada tela (L-25). O da página do link é o `api.js`
// dela; o do Playground fala com o rascunho. Um transporte tem:
// - `enviar(texto)`, `enviarComAnexos(texto, arquivos)` e `enviarNotaDeVoz(audioId, arquivos,
//   subidos)`, que devolvem `{ request_id }`. A nota de voz leva junto a foto e o PDF escolhidos
//   (A-16), quando há; `subidos` é o que `subirAnexos` já subiu para ela;
// - `subirAnexos(arquivos)`, opcional: o transporte que sobe o arquivo em dois passos (o do
//   Playground) começa o upload enquanto a nota de voz é transcrita;
// - `resultado(pedido)`, que devolve o turno no formato da página (`status`, `partes`,
//   `imagens`, `anexos`, `transferencia`, `funil`, `nao_lidos`, `erro`, `respondida_em`). Os
//   `anexos`, quando vêm, são os da pergunta como o servidor os guardou, e tomam o lugar da
//   miniatura local; os `nao_lidos` são `{ nome, motivo }`;
// - `subirAudio(arquivo, duracaoMs)`, que devolve `{ audio_id }`, e `audio(audioId)`, que devolve
//   `{ estado, texto, motivo }`;
// - `recomecar()`;
// - `saida(e)`, opcional: o que tira a pessoa da conversa (ver `saidaDaPagina`, o padrão). O
//   Playground fica no painel, e não tem página inteira a trocar: o dele devolve null, e a falha
//   fica no turno.
// As falhas chegam como as do axios, com `response.status` e `response.data.erro`; o
// `response.data.detalhe`, quando vem, é o motivo da recusa em texto, que só a equipe vê.
export const ESPERA_MAXIMA_MS = 100000;
export const INTERVALO_MS = 1000;

export const esperar = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

// As respostas que mudam a página inteira, e não só o turno. `modo_trocado` é o link que mudou de
// modo com a página aberta: ela recarrega e segue no modo novo.
export const saidaDaPagina = e => {
  const status = e?.response?.status;
  const erro = e?.response?.data?.erro;
  if (status === 410) return 'inativo';
  if (status === 401) return 'sem_sessao';
  if (status === 409 && erro === 'agente_fora_do_ar') return 'fora_do_ar';
  if (status === 409 && erro === 'modo_trocado') return 'modo_trocado';
  if (status === 409 && erro === 'canal_indisponivel')
    return 'canal_indisponivel';
  return null;
};

export const saidaPelo = (transporte, e) =>
  transporte.saida ? transporte.saida(e) : saidaDaPagina(e);

// Os motivos da foto e do PDF (L-19) que o servidor recusa com nome.
export const RECUSAS_DE_ARQUIVO = [
  'anexo_tipo',
  'anexo_grande',
  'anexos_demais',
  'texto_obrigatorio',
];

// A recusa que o reenvio resolve: a referência do arquivo que venceu entre o upload e o turno, no
// Playground. Reenviar sobe o arquivo de novo.
const RECUSAS_QUE_O_REENVIO_RESOLVE = ['anexo_nao_encontrado'];

// Um 4xx que a conversa não conhece é recusa do servidor, e o mesmo envio seria recusado de novo:
// `recusada` não oferece reenviar. Sem resposta, ou com 5xx, é a rede, e reenviar pode dar certo.
// A caixa de teste que não serve (`canal_indisponivel`) tira a página do link da conversa; no
// Playground no modo canal ela fica no turno, com o motivo.
export const motivoDaFalha = (status, dados) => {
  if (status === 429) return 'ocupado';
  if (dados.erro === 'limite_do_visitante') return 'limite';
  if (dados.erro === 'mensagem_longa') return 'longa';
  if (dados.erro === 'canal_indisponivel') return 'canal_indisponivel';
  if (RECUSAS_DE_ARQUIVO.includes(dados.erro)) return dados.erro;
  if (
    status >= 400 &&
    status < 500 &&
    !RECUSAS_QUE_O_REENVIO_RESOLVE.includes(dados.erro)
  )
    return 'recusada';
  return 'rede';
};

// O envio do turno: a nota de voz pelo `audio_id`, com os arquivos que vão com ela; a mensagem com
// arquivo em multipart; o texto sozinho em JSON. `subidos` é o upload que o transporte de dois passos
// começou enquanto a nota era transcrita; o reenvio não o tem, e sobe de novo.
export const mandarAoServidor = (transporte, turno, subidos = null) => {
  if (turno.audioId && turno.arquivos?.length)
    return transporte.enviarNotaDeVoz(turno.audioId, turno.arquivos, subidos);
  if (turno.audioId) return transporte.enviarNotaDeVoz(turno.audioId);
  if (turno.arquivos?.length)
    return transporte.enviarComAnexos(turno.pergunta, turno.arquivos);
  return transporte.enviar(turno.pergunta);
};

// A pergunta como a página a guarda: o texto, os arquivos para reenviar e como eles aparecem.
export const novaPergunta = (pergunta, arquivos) => ({
  pergunta,
  criada_em: new Date().toISOString(),
  arquivos: guardados(arquivos),
  anexos: arquivos.map(paraATela),
});

// A nota de voz que acabou de ir toca na bolha (A-15): o arquivo fica só na memória desta aba, com
// a duração do gravador, e nada vai para disco nem para o S3 (D2). Recarregar a página a perde.
export const somDaGravacao = (arquivo, duracaoMs) => ({
  url: window.URL?.createObjectURL ? window.URL.createObjectURL(arquivo) : '',
  duracaoMs,
});

// A miniatura da bolha é do turno: sai quando o turno sai da tela (recomeçar, recarregar a
// conversa, encerrar) ou quando o resultado traz os anexos do servidor, que tomam o lugar dela. As
// do servidor não são `blob:` e ficam. A nota de voz da bolha (`som`) sai junto.
export const liberarBolhas = turnos =>
  turnos.forEach(turno =>
    liberarPrevias([...(turno.anexos || []), ...(turno.som ? [turno.som] : [])])
  );

// A nota de voz (L-15). O servidor espera o provedor de transcrição até 75 s, e a fila de testes é
// a de baixa prioridade: passando disto, a página desiste com o motivo que a produção usa quando o
// prazo estoura.
export const ESPERA_TRANSCRICAO_MS = 120000;

// Por que o upload da nota de voz não passou, no vocabulário da transcrição. O limite de envios do
// servidor (`erro: limite`) e a rede têm nome próprio, e também a gravação que o servidor não aceita
// como áudio e o runtime que não respondeu à conferência do teto de testes — este pelo status, porque
// o 502 do proxy chega sem o corpo do Rails. Os dois vinham do Playground, que os dizia à equipe. A
// caixa de teste que não serve, no Playground no modo canal, também (`motivoDaFalha`).
export const motivoDoUpload = e => {
  const dados = e?.response?.data || {};
  if (!e?.response) return 'rede';
  if (e.response.status === 429 && dados.erro === 'limite') return 'muitos';
  if (dados.erro === 'transcricao_desligada') return 'desligada';
  if (dados.erro === 'audio_invalido') return 'invalido';
  if (dados.erro === 'canal_indisponivel') return 'canal_indisponivel';
  if (e.response.status === 502) return 'servidor';
  return dados.motivo || 'erro';
};

// Consulta a transcrição a cada segundo até ela ficar pronta ou falhar. A queda no meio (rede, 5xx)
// não perde o áudio: a próxima consulta o encontra. Um 4xx é resposta: o áudio não existe mais. As
// respostas que mudam a página inteira sobem para quem chamou.
export async function aguardarTranscricao(transporte, audioId, encerrada) {
  const prazo = Date.now() + ESPERA_TRANSCRICAO_MS;
  while (!encerrada() && Date.now() < prazo) {
    try {
      // Sequencial de propósito: uma consulta por vez, a próxima só depois da anterior.
      // eslint-disable-next-line no-await-in-loop
      const resultado = await transporte.audio(audioId);
      if (resultado.estado !== 'pendente') return resultado;
    } catch (e) {
      if (saidaPelo(transporte, e)) throw e;
      if (e?.response?.status < 500)
        return { estado: 'falhou', motivo: 'erro' };
    }
    // eslint-disable-next-line no-await-in-loop
    await esperar(INTERVALO_MS);
  }
  return { estado: 'falhou', motivo: 'prazo' };
}

// `saida` diz o que tirou a pessoa da conversa: link inativo, agente fora do ar, modo trocado, canal
// indisponível ou sessão perdida. Quem usa a conversa decide a tela.
export function useConversaDoMotor(transporte) {
  // { chave, request_id, pergunta, status, partes, imagens, transferencia, funil, erro, falha,
  //   detalhe, espera, aguardando, audio: { estado, texto, motivo }, audioId, som: { url,
  //   duracaoMs }, criada_em, respondida_em }
  // As horas saem do servidor; até ele responder, a da pergunta é a do navegador.
  const turnos = ref([]);
  const saida = ref(null);
  const recomecando = ref(false);
  const falhaAoRecomecar = ref(false);
  // O limite por visitante, quando ele já foi atingido.
  const limite = ref(null);
  let encerrada = false;
  let sequencia = 0;

  const ocupada = computed(() =>
    turnos.value.some(turno =>
      ['transcrevendo', 'enviando', 'pending'].includes(turno.status)
    )
  );

  // Uma consulta que cai no caminho (rede, 5xx) não perde o turno: o resultado fica no runtime, e
  // a próxima o encontra. Um 4xx é resposta, e encerra; o `detalhe`, quando vem, é o motivo em
  // texto (o pedido que o runtime não acha mais), que a conversa mostra só à equipe.
  async function acompanhar(turno) {
    const prazo = Date.now() + ESPERA_MAXIMA_MS;
    let queda = null;
    while (!encerrada && Date.now() < prazo) {
      try {
        // Sequencial de propósito: uma consulta por vez, a próxima só depois da anterior.
        // eslint-disable-next-line no-await-in-loop
        const resposta = await transporte.resultado(turno.request_id);
        queda = null;
        if (resposta.status !== 'pending') {
          // Os anexos do servidor trocam os da bolha, e a miniatura `blob:` fica sem quem a mostre.
          if (resposta.anexos) liberarPrevias(turno.anexos || []);
          Object.assign(turno, resposta);
          return;
        }
      } catch (e) {
        const motivo = saidaPelo(transporte, e);
        if (motivo) {
          saida.value = motivo;
          return;
        }
        if (e?.response?.status < 500) {
          Object.assign(turno, {
            status: 'error',
            erro: 'perdido',
            detalhe: e.response.data?.detalhe || null,
          });
          return;
        }
        queda = e;
      }
      // eslint-disable-next-line no-await-in-loop
      await esperar(INTERVALO_MS);
    }
    // Se o prazo acabou com a rede fora, o motivo é a rede, não a demora do agente.
    if (!encerrada)
      Object.assign(turno, { status: 'error', erro: queda ? 'rede' : 'prazo' });
  }

  async function despachar(turno, subidos = null) {
    Object.assign(turno, {
      status: 'enviando',
      falha: null,
      espera: null,
      detalhe: null,
    });
    try {
      // A nota de voz vai pelo `audio_id`: o agente recebe o texto que o servidor guardou.
      const { request_id: pedido } = await mandarAoServidor(
        transporte,
        turno,
        subidos
      );
      Object.assign(turno, { request_id: pedido, status: 'pending' });
    } catch (e) {
      const motivo = saidaPelo(transporte, e);
      if (motivo) {
        saida.value = motivo;
        return;
      }
      const status = e?.response?.status;
      const dados = e?.response?.data || {};
      if (dados.erro === 'limite_do_visitante') limite.value = dados.limite;
      Object.assign(turno, {
        status: 'falhou',
        falha: motivoDaFalha(status, dados),
        falhaArquivo: dados.arquivo || null,
        detalhe: dados.detalhe || null,
        espera: dados.tentar_em || null,
        aguardando: Boolean(dados.tentar_em),
      });
      // Ocupado: o reenvio fica parado pelo tempo que o servidor pediu.
      if (dados.tentar_em)
        setTimeout(() => {
          turno.aguardando = false;
        }, dados.tentar_em * 1000);
      return;
    }
    await acompanhar(turno);
  }

  // O texto, e a foto ou o PDF que vieram com ele (L-19); um dos dois basta.
  function enviar(texto, arquivos = []) {
    const pergunta = (texto || '').trim();
    if ((!pergunta && !arquivos.length) || ocupada.value || limite.value)
      return;
    sequencia += 1;
    const turno = reactive({
      chave: `local-${sequencia}`,
      ...novaPergunta(pergunta, arquivos),
    });
    turnos.value.push(turno);
    despachar(turno);
  }

  // A nota de voz: sobe, espera a transcrição e, pronta, mostra o que o agente ouviu e segue o
  // turno como o de texto. Na falha, o motivo fica na bolha e nada vai ao agente. A bolha toca a
  // gravação desde já, e também quando o agente não ouviu. A foto e o PDF que vieram com ela (A-16)
  // vão no mesmo turno, com a transcrição no lugar do texto; no transporte de dois passos eles
  // sobem enquanto a nota é transcrita, e a falha desse upload aparece no envio do turno.
  async function enviarAudio({ arquivo, duracaoMs }, arquivos = []) {
    if (ocupada.value || limite.value) return;
    sequencia += 1;
    const turno = reactive({
      chave: `local-${sequencia}`,
      ...novaPergunta(null, arquivos),
      status: 'transcrevendo',
      audio: { estado: 'enviando' },
      som: somDaGravacao(arquivo, duracaoMs),
    });
    turnos.value.push(turno);
    const subidos =
      arquivos.length && transporte.subirAnexos
        ? transporte.subirAnexos(arquivos)
        : null;
    subidos?.catch(() => {});
    const falhar = motivo =>
      Object.assign(turno, {
        status: 'audio_falhou',
        audio: { estado: 'falhou', motivo },
      });
    try {
      const { audio_id: audioId } = await transporte.subirAudio(
        arquivo,
        duracaoMs
      );
      turno.audio = { estado: 'pendente' };
      const resultado = await aguardarTranscricao(
        transporte,
        audioId,
        () => encerrada
      );
      if (encerrada) return;
      if (resultado.estado !== 'pronto') {
        falhar(resultado.motivo);
        return;
      }
      Object.assign(turno, {
        audioId,
        audio: { estado: 'pronto', texto: resultado.texto },
      });
    } catch (e) {
      const motivo = saidaPelo(transporte, e);
      if (motivo) {
        saida.value = motivo;
        return;
      }
      const dados = e?.response?.data || {};
      if (dados.erro === 'limite_do_visitante') limite.value = dados.limite;
      falhar(motivoDoUpload(e));
      return;
    }
    await despachar(turno, subidos);
  }

  // A mesma pergunta, no mesmo lugar da conversa.
  function reenviar(turno) {
    if (ocupada.value || turno.aguardando) return;
    despachar(turno);
  }

  // Apaga a memória do agente e a conversa da tela. Se o servidor não confirmar, a tela não finge:
  // a conversa fica, e o aviso diz que não deu.
  async function recomecar() {
    if (recomecando.value || ocupada.value) return;
    recomecando.value = true;
    falhaAoRecomecar.value = false;
    try {
      await transporte.recomecar();
      liberarBolhas(turnos.value);
      turnos.value = [];
    } catch (e) {
      const motivo = saidaPelo(transporte, e);
      if (motivo) saida.value = motivo;
      else falhaAoRecomecar.value = true;
    } finally {
      recomecando.value = false;
    }
  }

  // A conversa que o servidor guardou, ao reabrir a página. O turno que ainda esperava resposta
  // volta a ser consultado.
  function carregar(conversa = [], limiteAtingido = null) {
    liberarBolhas(turnos.value);
    // A nota de voz volta como o que o agente ouviu: a pergunta guardada é a transcrição.
    turnos.value = conversa.map(turno =>
      reactive({
        chave: turno.request_id,
        ...turno,
        ...(turno.veio_de_audio
          ? { audio: { estado: 'pronto', texto: turno.pergunta } }
          : {}),
      })
    );
    limite.value = limiteAtingido;
    turnos.value
      .filter(turno => turno.status === 'pending')
      .forEach(turno => acompanhar(turno));
  }

  function encerrar() {
    encerrada = true;
    liberarBolhas(turnos.value);
  }

  return {
    turnos,
    saida,
    ocupada,
    recomecando,
    falhaAoRecomecar,
    limite,
    enviar,
    enviarAudio,
    reenviar,
    recomecar,
    carregar,
    encerrar,
  };
}
